import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals";
import { computed, inject, Signal } from "@angular/core";
import { SeSecurityStore } from "./se.security.store";
import { RoleRights } from "./securityConfig/role.rights";
import { AccessLevel, AccessLevels, isMinFls } from "../../pojo/Fls";
import { s25Resource } from "../../util/s25.resource";
import { RoleRightsConfigService } from "./securityConfig/role.rights.config.service";
import { LoginStore } from "../../stores/login.store";
import { OrgStatusEffect, OrgStatusFunction } from "./se.org.status.service";
import { S25Object } from "../../util/core/s25.object";
import { OlsService } from "../../services/ols.service";
import { Item } from "../../pojo/Item";
import { Security } from "../../pojo/Security";

type OrgSecurityRequirement = {
    right: RoleRights.RoleRightsList;
    access: AccessLevel;
    /** Org status function that, when it blocks access, denies the flag regardless of rights. */
    statusFunction?: OrgStatusFunction;
};

const {
    SE_EVENT_REQUEST,
    SE_EVENT_ATTENDEES,
    SE_EVENT_REGISTRATION,
    SE_EVENT_SURVEY,
    SE_BUDGET,
    SE_FORMS_MANAGE,
    SE_ORG_NEWS,
    SE_ORG_FILESTORE,
    SE_ORG_SECURITY,
    SE_ORG_LAYOUTS,
    SE_ORG_EMAIL,
    SE_ORG_CONTACTS,
    SE_ORG_EDIT,
} = RoleRights.RoleRightsList;

const OrgSecurityRequirements = {
    canViewAttendees: { right: SE_EVENT_ATTENDEES, access: AccessLevels.Read },
    canManageAttendees: { right: SE_EVENT_ATTENDEES, access: AccessLevels.Edit },
    canViewRegistration: { right: SE_EVENT_REGISTRATION, access: AccessLevels.Read },
    canManageRegistration: { right: SE_EVENT_REGISTRATION, access: AccessLevels.Edit },
    canCreateEvent: { right: SE_EVENT_REQUEST, access: AccessLevels.Edit, statusFunction: "CREATE_EVENTS" },
    canViewSurveyConfig: { right: SE_EVENT_SURVEY, access: AccessLevels.Read },
    canManageSurveyConfig: { right: SE_EVENT_SURVEY, access: AccessLevels.Edit },
    canViewBudget: { right: SE_BUDGET, access: AccessLevels.Read, statusFunction: "FUNDING_REQUESTS" },
    canManageBudgetPlan: { right: SE_BUDGET, access: AccessLevels.Full, statusFunction: "FUNDING_REQUESTS" },
    canManageForms: { right: SE_FORMS_MANAGE, access: AccessLevels.Full },
    canViewForms: { right: SE_FORMS_MANAGE, access: AccessLevels.Read },
    canManageNews: { right: SE_ORG_NEWS, access: AccessLevels.Full, statusFunction: "MANAGE_NEWS" },
    canViewOrgFilestore: { right: SE_ORG_FILESTORE, access: AccessLevels.Read },
    canManageOrgFilestore: { right: SE_ORG_FILESTORE, access: AccessLevels.Full },
    canViewOrgSecurity: { right: SE_ORG_SECURITY, access: AccessLevels.Read },
    canEditOrgSecurity: { right: SE_ORG_SECURITY, access: AccessLevels.Full },
    canManageLayouts: { right: SE_ORG_LAYOUTS, access: AccessLevels.Full },
    canViewOrgEmail: { right: SE_ORG_EMAIL, access: AccessLevels.Read },
    canManageOrgEmailTemplates: { right: SE_ORG_EMAIL, access: AccessLevels.Full },
    canViewMembers: { right: SE_ORG_CONTACTS, access: AccessLevels.Read, statusFunction: "MANAGE_MEMBERS" },
    canManageMembers: { right: SE_ORG_CONTACTS, access: AccessLevels.Full, statusFunction: "MANAGE_MEMBERS" },
    canLogServiceHours: { right: SE_ORG_CONTACTS, access: AccessLevels.Read, statusFunction: "LOG_SERVICE_HOURS" },
    canApproveServiceHours: { right: SE_ORG_CONTACTS, access: AccessLevels.Full },
    canEditOrg: { right: SE_ORG_EDIT, access: AccessLevels.Full, statusFunction: "EDIT_ORG_DETAILS" },
} as const satisfies Record<string, OrgSecurityRequirement>;

type OrgSecurityRequirementFlag = keyof typeof OrgSecurityRequirements;
export type LayoutSecurityFlag = OrgSecurityRequirementFlag | "canViewOrgPage";

function isStatusFunctionBlocked(statusEffects: OrgStatusEffect[], functionCode: OrgStatusFunction): boolean {
    return statusEffects.find((entry) => entry.function === functionCode)?.blocksAccess ?? false;
}

function olsEditRightToAccessLevel(edit: Security.right | undefined): AccessLevel | undefined {
    if (edit === undefined || edit === "notSet") {
        return undefined;
    }
    return edit as AccessLevel;
}

function orgWriteAllowedByOls(
    hasFlsOverride: boolean,
    olsEditRight: Security.right | undefined,
    olsKnown: boolean,
    roleRequirementMet: boolean | undefined,
    statusBlocked: boolean,
): boolean | undefined {
    if (hasFlsOverride) {
        return true;
    }
    // Status denials apply at every OLS level. Only Student Engagement Full overrides them.
    if (statusBlocked) {
        return false;
    }
    if (!olsKnown) {
        return undefined;
    }
    const olsEditLevel = olsEditRightToAccessLevel(olsEditRight);
    if (olsEditLevel === undefined) {
        return undefined;
    }
    if (isMinFls(olsEditLevel, AccessLevels.Edit)) {
        return true;
    }
    if (roleRequirementMet && isMinFls(olsEditLevel, AccessLevels.Read)) {
        return true;
    }
    return false;
}

export const SeOrgSecurityStore = signalStore(
    withState<{ orgId: number | undefined; statusEffects: OrgStatusEffect[]; apiStatusMessages: string[] }>({
        orgId: undefined,
        statusEffects: [],
        apiStatusMessages: [],
    }),
    withProps(() => ({
        _loginStore: inject(LoginStore),
    })),
    withProps((store) => ({
        _secStore: inject(SeSecurityStore),
        _rolesResource: s25Resource({
            params: () => store.orgId(),
            loader: async ({ params: orgId, abortSignal }) => {
                return await RoleRightsConfigService.getUserRoles(orgId, { abortSignal });
            },
        }),
        _orgEditOlsResource: s25Resource({
            if: () => store._loginStore.loggedIn(),
            params: () => {
                const orgId = store.orgId();
                return typeof orgId === "number" && Number.isFinite(orgId) ? orgId : undefined;
            },
            loader: async ({ params: orgId, abortSignal }) => {
                const accessItem = await OlsService.getEditAccessItem(orgId, Item.Ids.Organization);
                if (abortSignal.aborted) {
                    throw new DOMException("Aborted", "AbortError");
                }
                return accessItem?.OLS?.[0]?.edit;
            },
        }),
    })),
    withMethods((store) => ({
        setOrgId: (orgId: number | undefined) => patchState(store, { orgId }),
        setStatusEffects: (statusEffects: OrgStatusEffect[]) => patchState(store, { statusEffects }),
        setStatusMessages: (statusMessages: string[]) => patchState(store, { apiStatusMessages: statusMessages }),
        setOrgStatusPayload: (statusEffects: OrgStatusEffect[], statusMessages: string[]) =>
            patchState(store, {
                statusEffects,
                apiStatusMessages: statusMessages ?? [],
            }),
    })),
    withComputed((store) => {
        const isStatusBlocked = (functionCode: OrgStatusFunction) =>
            isStatusFunctionBlocked(store.statusEffects(), functionCode);

        const fromRequirement = ({ right, access, statusFunction }: OrgSecurityRequirement) => {
            const hasRight = store._secStore.orgAccessSignal(store.orgId, right, access);
            if (!statusFunction) {
                return hasRight;
            }
            return computed(() => store._secStore.hasFLSOverride() || (hasRight() && !isStatusBlocked(statusFunction)));
        };

        const requirementFlags = S25Object.fromEntries(
            S25Object.entries(OrgSecurityRequirements).map(([flag, requirement]) => [
                flag,
                fromRequirement(requirement),
            ]),
        ) as Record<OrgSecurityRequirementFlag, Signal<boolean | undefined>>;

        const olsEditLevel = computed(() => store._orgEditOlsResource.value());
        const olsEditKnown = computed(
            () => store._loginStore.loggedIn() && store._orgEditOlsResource.resource.hasValue(),
        );

        const canEditOrg = computed(() => {
            const allowed = orgWriteAllowedByOls(
                store._secStore.hasFLSOverride(),
                olsEditLevel(),
                olsEditKnown(),
                requirementFlags.canEditOrg(),
                isStatusBlocked(OrgSecurityRequirements.canEditOrg.statusFunction),
            );
            return allowed ?? false;
        });

        const canManageMembers = computed(() => {
            const allowed = orgWriteAllowedByOls(
                store._secStore.hasFLSOverride(),
                olsEditLevel(),
                olsEditKnown(),
                requirementFlags.canManageMembers(),
                isStatusBlocked(OrgSecurityRequirements.canManageMembers.statusFunction),
            );
            return allowed ?? false;
        });

        return {
            ...requirementFlags,
            canEditOrg,
            canManageMembers,
            canViewOrgPage: computed(() => !isStatusBlocked("SHOW_ORG_PAGE")),
            statusMessages: computed(() => {
                const fromApi = store.apiStatusMessages();
                if (fromApi?.length) {
                    return fromApi;
                }
                return store
                    .statusEffects()
                    .filter((effect) => effect.showMessage && effect.message)
                    .map((effect) => effect.message as string);
            }),
            orgRoles: () => store._rolesResource.value(),
            orgRolesLoading: () => store._rolesResource.resource.isLoading(),
        };
    }),
    withMethods((store) => ({
        /**
         * Evaluates a flag for a hypothetical role instead of the viewer: the role's level on the
         * flag's right (supplied by the caller), plus the same org-status blocks the viewer gets.
         */
        allowsForRole: (
            flag: LayoutSecurityFlag,
            levelForRight: (right: RoleRights.RoleRightsList) => AccessLevel,
        ): boolean => {
            if (flag === "canViewOrgPage") return store.canViewOrgPage();
            const requirement: OrgSecurityRequirement = OrgSecurityRequirements[flag];
            const { statusFunction } = requirement;
            if (statusFunction && isStatusFunctionBlocked(store.statusEffects(), statusFunction)) return false;
            return isMinFls(levelForRight(requirement.right), requirement.access);
        },
    })),
);
